import { PaletteMode } from "@mui/material";
import { createTheme, ThemeOptions } from "@mui/material/styles";

// Extend Material-UI's Button variants with app-specific button roles.
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    tertiary: true;
    inlineText: true;
    text: true;
  }
}

// Extend ButtonClasses to include new variants
declare module "@mui/material/Button/buttonClasses" {
  interface ButtonClasses {
    tertiary: string;
    inlineText: string;
    text: string;
  }
}

// Extend Material-UI's Palette and PaletteOptions to include custom colors
declare module "@mui/material/styles" {
  interface Palette {
    custom: {
      primary: string;
      primaryHover: string;
      primaryPressed: string;
      primaryDisabled: string;
      primarySecondaryHover: string;
      primarySecondaryPressed: string;
      secondary: string;
      secondaryHover: string;
      secondaryPressed: string;
      secondaryDisabled: string;
      secondarySecondaryHover: string;
      secondarySecondaryPressed: string;
      destructive: string;
      destructiveHover: string;
      destructivePressed: string;
      destructiveDisabled: string;
      destructiveSecondaryHover: string;
      destructiveSecondaryPressed: string;
      background: string;
      backgroundPaper: string;
      textPrimary: string;
      textSecondary: string;
      divider: string;
      gray: {
        0: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
      };
      blue: {
        0: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
      };
      cyan: {
        0: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
      };
      purple: {
        0: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
      };
      yellow: {
        0: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
      };
      red: {
        0: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
      };
      emerald: {
        0: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
      };
      lime: {
        0: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
      };
      buy: {
        primary: string;
        hover: string;
        pressed: string;
        disabled: string;
        secondaryHover: string;
        secondaryPressed: string;
      };
      sell: {
        primary: string;
        hover: string;
        pressed: string;
        disabled: string;
        secondaryHover: string;
        secondaryPressed: string;
      };
      index: {
        good: string;
        fair: string;
        poor: string;
        complete: string;
        bgGood: string;
        bgFair: string;
        bgBad: string;
        bgComplete: string;
      };
      others: {
        logoBlackWhite: string;
        logoBrandColor: string;
        modalBackgroundBlack: string;
        modalBackgroundWhite: string;
        messageBackgroundBlack: string;
        brandSecondColor: string;
      };
    };
  }

  interface PaletteOptions {
    custom?: Palette["custom"];
  }
}

// Figma color tokens (light/dark)
const colorTokens = {
  light: {
    primary: "#1868DB",
    primaryHover: "#1558BC",
    primaryPressed: "#123263",
    primaryDisabled: "#D5DDE5",
    primarySecondaryHover: "#ECF3FC",
    primarySecondaryPressed: "#E3EDFA",
    secondary: "#1868DB",
    secondaryHover: "#1558BC",
    secondaryPressed: "#123263",
    secondaryDisabled: "#D5DDE5",
    secondarySecondaryHover: "#ECF3FC",
    secondarySecondaryPressed: "#E3EDFA",
    destructive: "#FE5050",
    destructiveHover: "#D72A34",
    destructivePressed: "#CA2230",
    destructiveDisabled: "#F09A9D",
    destructiveSecondaryHover: "#FEF7F7",
    destructiveSecondaryPressed: "#FCE7E7",
    background: "#F7F8F9",
    backgroundPaper: "#FFFFFF",
    textPrimary: "#16203A",
    textSecondary: "#1868DB",
    divider: "#E8ECEF",
    gray: {
      0: "#F7F8F9",
      100: "#F1F2F4",
      200: "#E8ECEF",
      300: "#D0D7DB",
      400: "#C5C8CE",
      500: "#ACB4BB",
      600: "#646F7C",
      700: "#374553",
      800: "#28323C",
      900: "#161D24",
    },
    blue: {
      0: "#F5F8FE",
      100: "#E7F1FE",
      200: "#C6DFFE",
      300: "#A2CCFA",
      400: "#6DA2F2",
      500: "#3479EB",
      600: "#2A5FC7",
      700: "#224CA8",
      800: "#183287",
      900: "#071C63",
    },
    cyan: {
      0: "#F5FDFF",
      100: "#E8FCFF",
      200: "#C7F8FF",
      300: "#A2EFFA",
      400: "#6DE1F2",
      500: "#34D2EB",
      600: "#2AADC7",
      700: "#228EA8",
      800: "#186D87",
      900: "#074963",
    },
    purple: {
      0: "#FAF5FF",
      100: "#F4E8FE",
      200: "#E4C7FE",
      300: "#CEA2FA",
      400: "#B26DF2",
      500: "#9333EB",
      600: "#7F2AC7",
      700: "#6B21A8",
      800: "#561887",
      900: "#3B0763",
    },
    yellow: {
      0: "#FFFDF5",
      100: "#FFF9E8",
      200: "#FFF0C7",
      300: "#FAE5A2",
      400: "#F2D66D",
      500: "#EBC434",
      600: "#C7A72A",
      700: "#A88E22",
      800: "#877018",
      900: "#634E07",
    },
    red: {
      0: "#FFF5F5",
      100: "#FFE8E8",
      200: "#FFC7C7",
      300: "#FAA2A2",
      400: "#F26F6D",
      500: "#EB3434",
      600: "#C72F2A",
      700: "#A82222",
      800: "#871818",
      900: "#630707",
    },
    emerald: {
      0: "#F5FFF9",
      100: "#E8FFF4",
      200: "#C7FFE5",
      300: "#A2FADA",
      400: "#6DF2C3",
      500: "#34EBAF",
      600: "#2AC795",
      700: "#22A87F",
      800: "#188767",
      900: "#076346",
    },
    lime: {
      0: "#FCFEF5",
      100: "#F8FFE8",
      200: "#EBFFC7",
      300: "#D9FAA2",
      400: "#BEF26D",
      500: "#A2EB34",
      600: "#86C72A",
      700: "#6EA822",
      800: "#578719",
      900: "#3F6307",
    },
    buy: {
      primary: "#FE5050",
      hover: "#D72A34",
      pressed: "#CA2230",
      disabled: "#FFA7A7",
      secondaryHover: "#FFE9E9",
      secondaryPressed: "#FFDCDC",
    },
    sell: {
      primary: "#3B6EFF",
      hover: "#2453D9",
      pressed: "#2851C6",
      disabled: "#9DB6FF",
      secondaryHover: "#ECF3FF",
      secondaryPressed: "#E2EBFF",
    },
    index: {
      good: "#38C976",
      fair: "#FFA23A",
      poor: "#FE5050",
      complete: "#3B6EFF",
      bgGood: "#E1F7EB",
      bgFair: "#FFEBD5",
      bgBad: "#FFE5E5",
      bgComplete: "#ECF3FF",
    },
    others: {
      logoBlackWhite: "#000000",
      logoBrandColor: "#F7931A",
      modalBackgroundBlack: "#00000099",
      modalBackgroundWhite: "#FFFFFF52",
      messageBackgroundBlack: "#000000A3",
      brandSecondColor: "#F7931A",
    },
  },
  dark: {
    primary: "#1868DB",
    primaryHover: "#1558BC",
    primaryPressed: "#123263",
    primaryDisabled: "#4A4238",
    primarySecondaryHover: "#162333",
    primarySecondaryPressed: "#16263A",
    secondary: "#1868DB",
    secondaryHover: "#1558BC",
    secondaryPressed: "#123263",
    secondaryDisabled: "#4A4238",
    secondarySecondaryHover: "#162333",
    secondarySecondaryPressed: "#16263A",
    destructive: "#E9343A",
    destructiveHover: "#D72A34",
    destructivePressed: "#CA2230",
    destructiveDisabled: "#82292D",
    destructiveSecondaryHover: "#242022",
    destructiveSecondaryPressed: "#4D161C",
    background: "#000000",
    backgroundPaper: "#16203A",
    textPrimary: "#F8F9FA",
    textSecondary: "#BFDEFF",
    divider: "#374553",
    gray: {
      0: "#000000",
      100: "#28323C",
      200: "#374553",
      300: "#646F7C",
      400: "#737D88",
      500: "#868E96",
      600: "#CACED2",
      700: "#DFE3E5",
      800: "#F1F2F4",
      900: "#F8F9FA",
    },
    blue: {
      0: "#071C63",
      100: "#183287",
      200: "#224CA8",
      300: "#2A5FC7",
      400: "#3479EB",
      500: "#6DA2F2",
      600: "#A2CCFA",
      700: "#C6DFFE",
      800: "#CFE4E8",
      900: "#F5F8FE",
    },
    cyan: {
      0: "#074963",
      100: "#186D87",
      200: "#228EA8",
      300: "#2AADC7",
      400: "#34D2EB",
      500: "#6DE1F2",
      600: "#A2EFFA",
      700: "#C7F8FF",
      800: "#E8FCFF",
      900: "#F5FDFF",
    },
    purple: {
      0: "#3B0763",
      100: "#561887",
      200: "#6B21A8",
      300: "#7F2AC7",
      400: "#9333EB",
      500: "#AB5EF2",
      600: "#CEA2FA",
      700: "#E4C7FE",
      800: "#F4E8FE",
      900: "#FAF5FF",
    },
    yellow: {
      0: "#634E07",
      100: "#877018",
      200: "#A88E22",
      300: "#C7A72A",
      400: "#EBC434",
      500: "#F2D66D",
      600: "#FAE5A2",
      700: "#FFF0C7",
      800: "#FFF9E8",
      900: "#FFFDF5",
    },
    red: {
      0: "#630707",
      100: "#871818",
      200: "#A82222",
      300: "#C72F2A",
      400: "#EB3434",
      500: "#F26F6D",
      600: "#FAA2A2",
      700: "#FFC7C7",
      800: "#FFE8E8",
      900: "#FFF5F5",
    },
    emerald: {
      0: "#076346",
      100: "#188767",
      200: "#22A87F",
      300: "#2AC795",
      400: "#34EBAF",
      500: "#6DF2C3",
      600: "#A2FADA",
      700: "#C7FFE5",
      800: "#E8FFF4",
      900: "#F5FFF9",
    },
    lime: {
      0: "#3F6307",
      100: "#578719",
      200: "#6EA822",
      300: "#86C72A",
      400: "#A2EB34",
      500: "#BEF26D",
      600: "#D9FAA2",
      700: "#EBFFC7",
      800: "#F8FFE8",
      900: "#FCFEF5",
    },
    buy: {
      primary: "#FE5050",
      hover: "#D72A34",
      pressed: "#CA2230",
      disabled: "#FFA7A7",
      secondaryHover: "#3C2B2B",
      secondaryPressed: "#3C2B2B",
    },
    sell: {
      primary: "#3B6EFF",
      hover: "#2453D9",
      pressed: "#2851C6",
      disabled: "#9DB6FF",
      secondaryHover: "#2B303F",
      secondaryPressed: "#2B303F",
    },
    index: {
      good: "#38C976",
      fair: "#FFA23A",
      poor: "#FE5050",
      complete: "#3B6EFF",
      bgGood: "#1B3730",
      bgFair: "#493A29",
      bgBad: "#39252B",
      bgComplete: "#2B303F",
    },
    others: {
      logoBlackWhite: "#FFFFFF",
      logoBrandColor: "#F7931A",
      modalBackgroundBlack: "#00000099",
      modalBackgroundWhite: "#FFFFFF52",
      messageBackgroundBlack: "#FFFFFFA3",
      brandSecondColor: "#F7931A",
    },
  },
};

export const getDesignTokens = (mode: PaletteMode): ThemeOptions => {
  const c = colorTokens[mode];
  return {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1024,
        xl: 1536,
      },
    },
    palette: {
      mode,
      primary: {
        main: c.primary,
        light: c.primaryHover,
        dark: c.primaryPressed,
        contrastText: "#fff",
      },
      secondary: {
        main: c.secondary,
        light: c.secondaryHover,
        dark: c.secondaryPressed,
        contrastText: "#fff",
      },
      error: {
        main: c.destructive,
        light: c.destructiveHover,
        dark: c.destructivePressed,
        contrastText: "#fff",
      },
      background: {
        default: c.background,
        paper: c.backgroundPaper,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
      },
      divider: c.divider,
      custom: {
        ...c,
        gray: c.gray,
        blue: c.blue,
        cyan: c.cyan,
        purple: c.purple,
        yellow: c.yellow,
        red: c.red,
        emerald: c.emerald,
        lime: c.lime,
        buy: c.buy,
        sell: c.sell,
        index: c.index,
        others: c.others,
      },
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: "Pretendard, Inter, Arial, sans-serif",
      h1: {
        fontSize: "var(--Typo---Heading-H1, 3rem)",
        fontWeight: 700,
        color: c.gray[900],
      },
      h2: {
        fontSize: "var(--Typo---Heading-H2, 2rem)",
        fontWeight: 700,
        color: c.gray[900],
      },
      h3: {
        fontSize: "var(--Typo---Heading-H3, 1.5rem)",
        fontWeight: 600,
        color: c.gray[900],
      },
      h4: {
        fontSize: "var(--Typo---Heading-H4, 1.25rem)",
        fontWeight: 600,
        color: c.gray[800],
      },
      h5: {
        fontSize: "var(--Typo---Heading-H5, 1rem)",
        fontWeight: 500,
        color: c.gray[800],
      },
      h6: {
        fontSize: "var(--Typo---Heading-H6, 1rem)",
        fontWeight: 500,
        color: c.gray[800],
      },
      body1: {
        fontSize: "var(--Typo---Paragraph-P1, 0.875rem)",
        fontWeight: 500,
        color: c.gray[700],
      },
      body2: {
        fontSize: "var(--Typo---Paragraph-P2, 0.875rem)",
        color: c.gray[700],
      },
      subtitle1: {
        fontSize: "var(--Typo---Paragraph-P3, 0.75rem)",
        color: c.gray[700],
      },
      subtitle2: {
        fontSize: "var(--Typo---Paragraph-P4, 0.75rem)",
        color: c.gray[700],
      },
      caption: {
        fontSize: "var(--Typo---Paragraph-P5, 0.6875rem)",
        color: c.gray[700],
      },
      overline: {
        fontSize: "var(--Typo---Paragraph-P6, 0.6875rem)",
        color: c.gray[700],
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `


          :root {
            --Typo---Heading-H1: 3rem;
            --Typo---Heading-H2: 2rem;
            --Typo---Heading-H3: 1.5rem;
            --Typo---Heading-H4: 1.25rem;
            --Typo---Heading-H5: 1rem;
            --Typo---Heading-H6: 1rem;
            --Typo---Paragraph-P1: 0.875rem;
            --Typo---Paragraph-P2: 0.875rem;
            --Typo---Paragraph-P3: 0.75rem;
            --Typo---Paragraph-P4: 0.75rem;
            --Typo---Paragraph-P5: 0.6875rem;
            --Typo---Paragraph-P6: 0.6875rem;
            --Typo---Button-Medium: 16px;
            --Typo---Button-Small: 16px;
            --Typo---Button-Xsmall: 14px;
            --Typo---Button-Xxsmall: 12px;
            --Input-Radius-Medium: 8px;
            --Input-Radius-Small: 6px;
            --Input-Radius-XSmall: 4px;
            --Input-Radius-Round: 100px;
            --Card-Radius-Large: 10px;
            --Card-Radius-Medium: 8px;
            --Card-Radius-Small: 6px;
            --Card-Radius-Round: 100px;
            --Margin-Padding-4XS: 2px;
            --Margin-Padding-3XS: 4px;
            --Margin-Padding-2XS: 8px;
            --Margin-Padding-XS: 12px;
            --Margin-Padding-S: 16px;
            --Margin-Padding-MS: 20px;
            --Margin-Padding-M: 24px;
            --Margin-Padding-ML: 32px;
            --Margin-Padding-L: 40px;
            --Margin-Padding-XL: 48px;
            --Margin-Padding-2XL: 64px;
            --Margin-Padding-3XL: 80px;
            --Margin-Padding-4XL: 96px;
            --Margin-Padding-Page-without-Nav: 120px;
            --Margin-Padding-Page-with-Nav: 64px;
            --Component-Height-Button-M: 52px;
            --Component-Height-Button-S: 44px;
            --Component-Height-Button-XS: 36px;
            --Component-Height-Button-2XS: 28px;
            --Component-Height-Button-3XS: 20px;
            --Component-Height-Chip-M: 32px;
            --Component-Height-Chip-S: 24px;
            --Component-Height-Chip-XS: 20px;
            --Component-Height-Icon-XXL: 48px;
            --Component-Height-Icon-XL: 40px;
            --Component-Height-Icon-L: 32px;
            --Component-Height-Icon-M: 24px;
            --Component-Height-Icon-S: 20px;
            --Component-Height-Icon-XS: 16px;

            /* Aliases for scale-variable naming convention */
            --scale-variable-margin-padding-4xs: 2px;
            --scale-variable-margin-padding-3xs: 4px;
            --scale-variable-margin-padding-2xs: 8px;
            --scale-variable-margin-padding-XS: 12px;
            --scale-variable-margin-padding-s: 16px;
            --scale-variable-margin-padding-ms: 20px;
            --scale-variable-margin-padding-m: 24px;
            --scale-variable-margin-padding-ml: 32px;
            --scale-variable-margin-padding-l: 40px;
            --scale-variable-margin-padding-xl: 48px;
            --scale-variable-margin-padding-2xl: 64px;
            --scale-variable-margin-padding-3xl: 80px;
            --scale-variable-margin-padding-4xl: 96px;
            --scale-variable-component-height-button-m: 52px;
            --scale-variable-component-height-button-s: 44px;
            --scale-variable-component-height-button-xs: 36px;
            --scale-variable-component-height-chip-s: 24px;
            --scale-variable-component-height-chip-m: 32px;
            --scale-variable-input-radius-medium: 8px;
            --scale-variable-card-radius-medium: 8px;

            /* Color mode variables */
            --color-mode-gray-white-black: ${c.backgroundPaper};
            --color-mode-gray-gray-200: ${c.gray[200]};
            --color-mode-gray-gray-400: ${c.gray[400]};
            --color-mode-gray-gray-500: ${c.gray[500]};
            --color-mode-gray-gray-700: ${c.gray[700]};
            --color-mode-gray-gray-800: ${c.gray[800]};
            --color-mode-gray-gray-900: ${c.gray[900]};
            --color-mode-gray-gray-300: ${c.gray[300]};
            --color-mode-CTA-1-default: ${c.primary};
            --color-mode-CTA-2-default: ${c.secondary};

            /* Typography variables */
            --heading-h2-font-family: "Pretendard", Inter, Arial, sans-serif;
            --heading-h2-font-weight: 700;
            --heading-h2-font-size: 2rem;
            --heading-h2-line-height: 1.3;
            --heading-h2-letter-spacing: -0.01em;
            --heading-h2-font-style: normal;

            --heading-h3-font-family: "Pretendard", Inter, Arial, sans-serif;
            --heading-h3-font-weight: 600;
            --heading-h3-font-size: 1.5rem;
            --heading-h3-line-height: 1.4;
            --heading-h3-letter-spacing: 0em;
            --heading-h3-font-style: normal;

            --heading-h5-font-family: "Pretendard", Inter, Arial, sans-serif;
            --heading-h5-font-weight: 500;
            --heading-h5-font-size: 1rem;
            --heading-h5-line-height: 1.5;
            --heading-h5-letter-spacing: 0em;
            --heading-h5-font-style: normal;

            --paragraph-p1-font-family: "Pretendard", Inter, Arial, sans-serif;
            --paragraph-p1-font-weight: 500;
            --paragraph-p1-font-size: 0.875rem;
            --paragraph-p1-line-height: 1.5;
            --paragraph-p1-letter-spacing: 0em;
            --paragraph-p1-font-style: normal;

            --paragraph-p2-font-family: "Pretendard", Inter, Arial, sans-serif;
            --paragraph-p2-font-weight: 400;
            --paragraph-p2-font-size: 0.875rem;
            --paragraph-p2-line-height: 1.5;
            --paragraph-p2-letter-spacing: 0em;
            --paragraph-p2-font-style: normal;

            --button-medium-font-family: "Pretendard", Inter, Arial, sans-serif;
            --button-medium-font-weight: 600;
            --button-medium-font-size: 1rem;
            --button-medium-line-height: 1.5;
            --button-medium-letter-spacing: 0em;
            --button-medium-font-style: normal;
          }

          @media (max-width: 600px) {
            :root {
              --Typo---Heading-H1: 2rem;
              --Typo---Heading-H2: 1.5rem;
              --Typo---Heading-H3: 1.25rem;
              --Typo---Heading-H4: 1.125rem;
              --Typo---Heading-H5: 1rem;
              --Typo---Heading-H6: 1rem;
              --Typo---Paragraph-P1: 0.875rem;
              --Typo---Paragraph-P2: 0.875rem;
              --Typo---Paragraph-P3: 0.75rem;
              --Typo---Paragraph-P4: 0.75rem;
              --Typo---Paragraph-P5: 0.6875rem;
              --Typo---Paragraph-P6: 0.6875rem;
              --Typo---Button-Medium: 16px;
              --Typo---Button-Small: 16px;
              --Typo---Button-Xsmall: 14px;
              --Typo---Button-Xxsmall: 12px;
              --Input-Radius-Medium: 10px;
              --Input-Radius-Small: 8px;
              --Input-Radius-XSmall: 8px;
              --Input-Radius-Round: 100px;
              --Card-Radius-Large: 10px;
              --Card-Radius-Medium: 8px;
              --Card-Radius-Small: 6px;
              --Card-Radius-Round: 100px;
              --Margin-Padding-4XS: 2px;
              --Margin-Padding-3XS: 4px;
              --Margin-Padding-2XS: 8px;
              --Margin-Padding-XS: 12px;
              --Margin-Padding-S: 12px;
              --Margin-Padding-MS: 16px;
              --Margin-Padding-M: 20px;
              --Margin-Padding-ML: 24px;
              --Margin-Padding-L: 32px;
              --Margin-Padding-XL: 40px;
              --Margin-Padding-2XL: 48px;
              --Margin-Padding-3XL: 64px;
              --Margin-Padding-4XL: 80px;
              --Margin-Padding-Page-without-Nav: 24px;
              --Margin-Padding-Page-with-Nav: 16px;
              --Component-Height-Button-M: 52px;
              --Component-Height-Button-S: 44px;
              --Component-Height-Button-XS: 36px;
              --Component-Height-Button-2XS: 28px;
              --Component-Height-Button-3XS: 20px;
              --Component-Height-Chip-M: 32px;
              --Component-Height-Chip-S: 24px;
              --Component-Height-Chip-XS: 20px;
              --Component-Height-Icon-XXL: 48px;
              --Component-Height-Icon-XL: 40px;
              --Component-Height-Icon-L: 32px;
              --Component-Height-Icon-M: 24px;
              --Component-Height-Icon-S: 20px;
              --Component-Height-Icon-XS: 16px;
            }
          }

          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
          }

          h1 {
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
            font-weight: 700;
            font-size: var(--Typo---Heading-H1);
            line-height: 1.2;
            letter-spacing: -0.02em;
            color: ${c.gray[900]};
          }
          h2 {
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
            font-weight: 600;
            font-size: var(--Typo---Heading-H2);
            line-height: 1.3;
            letter-spacing: -0.01em;
            color: ${c.gray[800]};
          }
          h3 {
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
            font-weight: 600;
            font-size: var(--Typo---Heading-H3);
            line-height: 1.4;
            color: ${c.gray[800]};
          }
          h4 {
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
            font-weight: 600;
            font-size: var(--Typo---Heading-H4);
            line-height: 1.4;
            color: ${c.gray[800]};
          }
          h5 {
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
            font-weight: 500;
            font-size: var(--Typo---Heading-H5);
            line-height: 1.5;
            color: ${c.gray[800]};
          }
          h6 {
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
            font-weight: 500;
            font-size: var(--Typo---Heading-H6);
            line-height: 1.5;
            color: ${c.gray[800]};
          }
          p {
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
            font-weight: 400;
            font-size: var(--Typo---Paragraph-P1);
            line-height: 1.5;
            letter-spacing: 0em;
            color: ${c.gray[700]};
          }

          @media (max-width: 600px) {
            h1 {
              font-size: var(--Typo---Heading-H1);
            }
            h2 {
              font-size: var(--Typo---Heading-H2);
            }
            h3 {
              font-size: var(--Typo---Heading-H3);
            }
            h4 {
              font-size: var(--Typo---Heading-H4);
            }
            h5 {
              font-size: var(--Typo---Heading-H5);
            }
            h6 {
              font-size: var(--Typo---Heading-H6);
            }
            p {
              font-size: var(--Typo---Paragraph-P1);
            }
          }

          body {
            font-family: "Pretendard", Inter, Arial, sans-serif !important;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-synthesis: none;
          }
        `,
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "inlineText" as "inlineText" },
            style: {
              minWidth: "auto",
              minHeight: "auto !important",
              height: "auto !important",
              padding: "0 !important",
              gap: 0,
              border: "none",
              backgroundColor: "transparent",
              color: c.primary,
              lineHeight: "inherit",
              "& .MuiButton-startIcon": {
                margin: 0,
                marginRight: "var(--Margin-Padding-2XS)",
              },
              "& .MuiButton-endIcon": {
                margin: 0,
              },
              "& .MuiTouchRipple-root": {
                display: "none",
              },
              "&:hover": {
                border: "none",
                backgroundColor: "transparent",
                color: c.primaryHover,
              },
              "&:active": {
                backgroundColor: "transparent",
                color: c.primaryPressed,
              },
              "&.Mui-disabled": {
                backgroundColor: "transparent",
                color: c.primaryDisabled,
              },
            },
          },
          {
            props: { variant: "tertiary" as "tertiary" },
            style: {
              backgroundColor: "transparent",
              color: c.primary,
              border: "none",
              padding: "var(--Margin-Padding-2XS) var(--Margin-Padding-S)",
              minHeight: "52px",
              "&:hover": {
                backgroundColor: c.primarySecondaryHover,
                border: "none",
              },
              "&:active": {
                backgroundColor: c.primarySecondaryPressed,
              },
              "&.Mui-disabled": {
                backgroundColor: "transparent",
                color: c.primaryDisabled,
                cursor: "not-allowed",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: c.primaryDisabled,
                },
                "&:active": {
                  backgroundColor: "transparent",
                  color: c.primaryDisabled,
                },
              },
              "&.MuiButton-sizeSmall": {
                minHeight: "44px",
                padding: "var(--Margin-Padding-3XS) var(--Margin-Padding-S)",
              },
              "&.MuiButton-sizeXxsmall": {
                minHeight: "28px",
                height: "28px",
                padding: "var(--Margin-Padding-4XS) var(--Margin-Padding-2XS)",
                fontSize: "12px",
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            borderRadius: "var(--Input-Radius-Medium)",
            minHeight: "44px", // Small size default
            border: `1px solid ${c.primary}`,
            padding: "var(--Margin-Padding-3XS) var(--Margin-Padding-S)", // Medium size default
            fontWeight: 600,
            fontSize: "1rem", // 16px equivalent
            gap: "var(--Margin-Padding-2XS)",
            textTransform: "none",
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            boxShadow: "none",
            "& .MuiButton-startIcon": { margin: 0 },
            "& .MuiButton-endIcon": { margin: 0 },
            "&.MuiButton-sizeSmall": {
              borderRadius: "var(--Input-Radius-Small)",
            },
            "&.MuiButton-sizeXxsmall": {
              borderRadius: "var(--Input-Radius-XSmall)",
            },
            "&:hover": {
              boxShadow: "none",
            },
            "&:active": {
              boxShadow: "none",
            },
            "&:focus": {
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              boxShadow: "none",
            },
          },
          containedPrimary: {
            backgroundColor: c.primary,
            color: "#fff",
            "&:hover": {
              backgroundColor: c.primaryHover,
            },
            "&:active": {
              backgroundColor: c.primaryPressed,
            },
            "&.Mui-disabled": {
              border: "none",
              backgroundColor: c.primaryDisabled,
              color: c.backgroundPaper,
              cursor: "not-allowed",
              "&:hover": {
                backgroundColor: c.primaryDisabled,
                color: c.gray[600],
              },
              "&:active": {
                backgroundColor: c.primaryDisabled,
                color: c.gray[600],
              },
            },
          },
          containedSecondary: {
            backgroundColor: c.secondary,
            color: "#fff",
            "&:hover": {
              backgroundColor: c.secondaryHover,
            },
            "&:active": {
              backgroundColor: c.secondaryPressed,
            },
            "&.Mui-disabled": {
              backgroundColor: c.secondaryDisabled,
              color: c.gray[600],
              cursor: "not-allowed",
              "&:hover": {
                backgroundColor: c.secondaryDisabled,
                color: c.gray[600],
              },
              "&:active": {
                backgroundColor: c.secondaryDisabled,
                color: c.gray[600],
              },
            },
          },
          outlinedPrimary: {
            borderColor: c.primary,
            color: c.primary,
            "&:hover": {
              borderColor: c.primaryHover,
              backgroundColor: c.primarySecondaryHover,
            },
            "&:active": {
              borderColor: c.primaryPressed,
              backgroundColor: c.primarySecondaryPressed,
            },
            "&.Mui-disabled": {
              border: "none",
              // borderColor: c.primaryDisabled,
              color: c.primaryDisabled,
              cursor: "not-allowed",
              "&:hover": {
                borderColor: c.primaryDisabled,
                backgroundColor: "transparent",
                color: c.primaryDisabled,
              },
              "&:active": {
                borderColor: c.primaryDisabled,
                backgroundColor: "transparent",
                color: c.primaryDisabled,
              },
            },
          },
          outlinedSecondary: {
            borderColor: c.secondary,
            color: c.secondary,
            "&:hover": {
              borderColor: c.secondaryHover,
              backgroundColor: c.secondarySecondaryHover,
            },
            "&:active": {
              borderColor: c.secondaryPressed,
              backgroundColor: c.secondarySecondaryPressed,
            },
            "&.Mui-disabled": {
              borderColor: c.secondaryDisabled,
              color: c.secondaryDisabled,
              cursor: "not-allowed",
              "&:hover": {
                borderColor: c.secondaryDisabled,
                backgroundColor: "transparent",
                color: c.secondaryDisabled,
              },
              "&:active": {
                borderColor: c.secondaryDisabled,
                backgroundColor: "transparent",
                color: c.secondaryDisabled,
              },
            },
          },
          containedError: {
            backgroundColor: c.destructive,
            color: "#fff",
            "&:hover": {
              backgroundColor: c.destructiveHover,
            },
            "&:active": {
              backgroundColor: c.destructivePressed,
            },
            "&.Mui-disabled": {
              backgroundColor: c.destructiveDisabled,
              color: c.gray[600],
              cursor: "not-allowed",
              "&:hover": {
                backgroundColor: c.destructiveDisabled,
                color: c.gray[600],
              },
              "&:active": {
                backgroundColor: c.destructiveDisabled,
                color: c.gray[600],
              },
            },
          },
          outlinedError: {
            borderColor: c.destructive,
            color: c.destructive,
            "&:hover": {
              borderColor: c.destructiveHover,
              backgroundColor: c.destructiveSecondaryHover,
            },
            "&:active": {
              borderColor: c.destructivePressed,
              backgroundColor: c.destructiveSecondaryPressed,
            },
            "&.Mui-disabled": {
              borderColor: c.destructiveDisabled,
              color: c.destructiveDisabled,
              cursor: "not-allowed",
              "&:hover": {
                borderColor: c.destructiveDisabled,
                backgroundColor: "transparent",
                color: c.destructiveDisabled,
              },
              "&:active": {
                borderColor: c.destructiveDisabled,
                backgroundColor: "transparent",
                color: c.destructiveDisabled,
              },
            },
          },
          // Text variant
          text: {
            backgroundColor: "transparent",
            color: c.primary,
            border: "none",
            padding: "0",
            minHeight: "52px",
            minWidth: "auto",
            "& .MuiTouchRipple-root": {
              display: "none",
            },
            "&:hover": {
              backgroundColor: "transparent !important",
              color: c.primaryHover,
            },
            "&:active": {
              backgroundColor: "transparent !important",
              color: c.primaryPressed,
            },
            "&.Mui-disabled": {
              backgroundColor: "transparent !important",
              color: c.primaryDisabled,
              cursor: "not-allowed",
              "&:hover": {
                backgroundColor: "transparent !important",
                color: c.primaryDisabled,
              },
              "&:active": {
                backgroundColor: "transparent !important",
                color: c.primaryDisabled,
              },
            },
            "&.MuiButton-sizeSmall": {
              minHeight: "44px",
              padding: "0",
            },
            "&.MuiButton-sizeXxsmall": {
              minHeight: "28px",
              height: "28px",
              padding: "0",
              fontSize: "12px",
            },
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            cursor: "pointer !important",
            "&.Mui-disabled": {
              pointerEvents: "none",
              cursor: "default !important",
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            "& .asterisk": {
              color: "#FF0000", // Red color for asterisk
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: "#9CA3AF", // Gray 400 as default color
            "&.Mui-checked": {
              color: "#1868DB", // Blue CTA when checked
            },
            "&:hover": {
              backgroundColor: "rgba(156, 163, 175, 0.04)", // Light gray hover
            },
            "&.Mui-checked:hover": {
              backgroundColor: "rgba(24, 104, 219, 0.04)", // Light blue hover when checked
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "var(--Card-Radius-Medium)",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            height: "var(--Component-Height-Chip-M)",
            borderRadius: "var(--Input-Radius-Round)",
            "&.MuiChip-sizeSmall": {
              height: "var(--Component-Height-Chip-S)",
            },
            "&.MuiChip-sizeXSmall": {
              height: "var(--Component-Height-Chip-XS)",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          // iOS Safari zooms the page when focused inputs are below 16px (e.g. size="small" = 14px).
          input: ({ theme }) => ({
            [theme.breakpoints.down("md")]: {
              fontSize: "16px !important",
            },
          }),
          inputMultiline: ({ theme }) => ({
            [theme.breakpoints.down("md")]: {
              fontSize: "16px !important",
            },
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            minHeight: "44px",
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            "& .MuiInputBase-root": {
              minHeight: "44px",
              backgroundColor: "#FFFFFF",
              fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: c.secondaryHover,
                borderWidth: "1px",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: c.primary,
                borderWidth: "2px",
              },
            },
            "& .MuiInputBase-input": {
              paddingTop: "var(--Margin-Padding-XS)",
              paddingBottom: "var(--Margin-Padding-XS)",
              fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
          },
          inputRoot: {
            height: "52px !important",
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            backgroundColor: "#FFFFFF",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: c.secondaryHover,
              borderWidth: "1px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: c.primary,
              borderWidth: "2px",
            },
          },
          option: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            padding: "var(--Margin-Padding-2XS) var(--Margin-Padding-XS)",
            "&:hover": {
              backgroundColor: c.primarySecondaryHover,
            },
            "&.Mui-focused": {
              backgroundColor: c.primarySecondaryHover,
            },
          },
          paper: {
            borderRadius: 8,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            border: `1px solid ${c.divider}`,
            marginTop: "var(--Margin-Padding-2XS)",
          },
          tag: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            borderRadius: 6,
            height: 28,
            borderWidth: 1,
            borderColor: c.gray[300],
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            minHeight: "44px",
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            backgroundColor: "#FFFFFF",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: c.secondaryHover,
              borderWidth: "1px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: c.primary,
              borderWidth: "2px",
            },
          },
          select: ({ theme }) => ({
            paddingTop: "var(--Margin-Padding-XS)",
            paddingBottom: "var(--Margin-Padding-XS)",
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            [theme.breakpoints.down("md")]: {
              fontSize: "16px !important",
            },
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            padding: "var(--Margin-Padding-2XS) var(--Margin-Padding-S)",
            "&:hover": {
              backgroundColor: c.primarySecondaryHover,
            },
            "&.Mui-selected": {
              backgroundColor: c.primarySecondaryHover,
              "&:hover": {
                backgroundColor: c.primarySecondaryPressed,
              },
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            "&.Mui-required .MuiInputLabel-asterisk": {
              color: "#FF0000", // Red color for asterisk
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontFamily: "Pretendard, Inter, Arial, sans-serif !important",
            "&.Mui-required .MuiFormLabel-asterisk": {
              color: "#FF0000", // Red color for asterisk
            },
          },
        },
      },
      MuiRadio: {
        defaultProps: {
          icon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="0.5"
                width="19"
                height="19"
                rx="9.5"
                stroke="#C5C8CE"
              />
            </svg>
          ),
          checkedIcon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="20" height="20" rx="10" fill="#1868DB" />
              <circle cx="10" cy="10" r="4.4" fill="white" />
            </svg>
          ),
        },
        styleOverrides: {
          root: {
            padding: "var(--Margin-Padding-2XS)",
            "&:hover": {
              backgroundColor: c.primarySecondaryHover,
            },
          },
        },
      },
    },
  };
};

export function createAppTheme(mode: PaletteMode) {
  return createTheme(getDesignTokens(mode));
}

// For backward compatibility
export const createCustomTheme = (darkMode: boolean) =>
  createAppTheme(darkMode ? "dark" : "light");
